import React from 'react';
import { theme } from '../styles/theme';
import { Button, Card, SectionTitle, Container } from '../components/ui';

const StyleGuidePage = () => {
  return (
    <div style={{ paddingTop: theme.spacing.xl, paddingBottom: theme.spacing.xxl }}>
      <Container>
        {/* Hero Section */}
        <div style={{ textAlign: 'center', marginBottom: theme.spacing.xxl }}>
          <h1 style={{
            fontFamily: theme.typography.fontFamily.primary,
            fontSize: theme.typography.fontSize['5xl'],
            color: theme.colors.primaryDark,
            marginBottom: theme.spacing.lg,
            letterSpacing: '0.1em',
          }}>
            WESTERN TRADING POST
          </h1>
          <p style={{
            fontFamily: theme.typography.fontFamily.secondary,
            fontSize: theme.typography.fontSize.lg,
            color: theme.colors.textSecondary,
            maxWidth: '600px',
            margin: '0 auto',
          }}>
            A warm, rustic Western decor storefront showcasing our design system
          </p>
        </div>

        {/* Color Palette */}
        <SectionTitle size="lg" align="center" withDecorative>
          Color Palette
        </SectionTitle>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: theme.spacing.md,
          marginBottom: theme.spacing.xxl,
        }}>
          {[
            { name: 'Primary', color: theme.colors.primary },
            { name: 'Primary Dark', color: theme.colors.primaryDark },
            { name: 'Rust', color: theme.colors.primaryLight },
            { name: 'CTA Burgundy', color: theme.colors.cta },
            { name: 'Accent', color: theme.colors.accent },
            { name: 'Background', color: theme.colors.background },
          ].map(({ name, color }) => (
            <Card key={name} padding="md">
              <div style={{
                width: '100%',
                height: '80px',
                backgroundColor: color,
                borderRadius: theme.borderRadius.md,
                marginBottom: theme.spacing.sm,
              }} />
              <p style={{
                fontFamily: theme.typography.fontFamily.secondary,
                fontSize: theme.typography.fontSize.sm,
                fontWeight: theme.typography.fontWeight.semibold,
                color: theme.colors.text,
                textAlign: 'center',
              }}>
                {name}
              </p>
              <p style={{
                fontFamily: theme.typography.fontFamily.secondary,
                fontSize: theme.typography.fontSize.xs,
                color: theme.colors.textSecondary,
                textAlign: 'center',
              }}>
                {color}
              </p>
            </Card>
          ))}
        </div>

        {/* Typography */}
        <SectionTitle size="lg" align="center" withDecorative>
          Typography
        </SectionTitle>
        <Card padding="xl" style={{ marginBottom: theme.spacing.xxl }}>
          <h1 style={{
            fontFamily: theme.typography.fontFamily.primary,
            fontSize: theme.typography.fontSize['4xl'],
            marginBottom: theme.spacing.md,
          }}>
            Heading 1 - Playfair Display
          </h1>
          <h2 style={{
            fontFamily: theme.typography.fontFamily.primary,
            fontSize: theme.typography.fontSize['3xl'],
            marginBottom: theme.spacing.md,
          }}>
            Heading 2 - Playfair Display
          </h2>
          <h3 style={{
            fontFamily: theme.typography.fontFamily.primary,
            fontSize: theme.typography.fontSize['2xl'],
            marginBottom: theme.spacing.md,
          }}>
            Heading 3 - Playfair Display
          </h3>
          <p style={{
            fontFamily: theme.typography.fontFamily.secondary,
            fontSize: theme.typography.fontSize.base,
            lineHeight: theme.typography.lineHeight.relaxed,
            color: theme.colors.text,
            marginBottom: theme.spacing.md,
          }}>
            Body text uses Lato or Open Sans - a clean, readable sans-serif font perfect for product descriptions and content. This paragraph demonstrates the normal line height and spacing.
          </p>
          <p style={{
            fontFamily: theme.typography.fontFamily.secondary,
            fontSize: theme.typography.fontSize.sm,
            color: theme.colors.textSecondary,
          }}>
            Small text for captions and secondary information
          </p>
        </Card>

        {/* Buttons */}
        <SectionTitle size="lg" align="center" withDecorative>
          Buttons
        </SectionTitle>
        <Card padding="xl" style={{ marginBottom: theme.spacing.xxl }}>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: theme.spacing.md,
            marginBottom: theme.spacing.lg,
          }}>
            <Button variant="primary" size="lg">Primary Button</Button>
            <Button variant="secondary" size="lg">Secondary Button</Button>
            <Button variant="cart" size="lg">Add to Cart</Button>
            <Button variant="outline" size="lg">Outline Button</Button>
            <Button variant="ghost" size="lg">Ghost Button</Button>
          </div>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: theme.spacing.md,
            marginBottom: theme.spacing.lg,
          }}>
            <Button variant="primary" size="md">Medium Primary</Button>
            <Button variant="cart" size="md">Medium Cart</Button>
            <Button variant="outline" size="md">Medium Outline</Button>
          </div>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: theme.spacing.md,
          }}>
            <Button variant="primary" size="sm">Small</Button>
            <Button variant="cart" size="sm">Small Cart</Button>
            <Button variant="outline" size="sm">Small Outline</Button>
            <Button variant="primary" size="sm" disabled>Disabled</Button>
            <Button variant="cart" size="sm" loading>Loading</Button>
          </div>
        </Card>

        {/* Cards */}
        <SectionTitle size="lg" align="center" withDecorative>
          Card Variants
        </SectionTitle>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: theme.spacing.lg,
          marginBottom: theme.spacing.xxl,
        }}>
          <Card variant="default" padding="lg">
            <h3 style={{
              fontFamily: theme.typography.fontFamily.primary,
              fontSize: theme.typography.fontSize.xl,
              marginBottom: theme.spacing.sm,
            }}>
              Default Card
            </h3>
            <p style={{
              fontFamily: theme.typography.fontFamily.secondary,
              fontSize: theme.typography.fontSize.sm,
              color: theme.colors.textSecondary,
            }}>
              Standard card with light border and subtle shadow
            </p>
          </Card>

          <Card variant="outlined" padding="lg">
            <h3 style={{
              fontFamily: theme.typography.fontFamily.primary,
              fontSize: theme.typography.fontSize.xl,
              marginBottom: theme.spacing.sm,
            }}>
              Outlined Card
            </h3>
            <p style={{
              fontFamily: theme.typography.fontFamily.secondary,
              fontSize: theme.typography.fontSize.sm,
              color: theme.colors.textSecondary,
            }}>
              Card with prominent border
            </p>
          </Card>

          <Card variant="elevated" padding="lg">
            <h3 style={{
              fontFamily: theme.typography.fontFamily.primary,
              fontSize: theme.typography.fontSize.xl,
              marginBottom: theme.spacing.sm,
            }}>
              Elevated Card
            </h3>
            <p style={{
              fontFamily: theme.typography.fontFamily.secondary,
              fontSize: theme.typography.fontSize.sm,
              color: theme.colors.textSecondary,
            }}>
              Card with enhanced shadow
            </p>
          </Card>

          <Card variant="parchment" padding="lg">
            <h3 style={{
              fontFamily: theme.typography.fontFamily.primary,
              fontSize: theme.typography.fontSize.xl,
              marginBottom: theme.spacing.sm,
            }}>
              Parchment Card
            </h3>
            <p style={{
              fontFamily: theme.typography.fontFamily.secondary,
              fontSize: theme.typography.fontSize.sm,
              color: theme.colors.textSecondary,
            }}>
              Card with beige/parchment background
            </p>
          </Card>
        </div>

        {/* Hover Cards */}
        <SectionTitle size="lg" align="center" withDecorative>
          Interactive Cards
        </SectionTitle>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: theme.spacing.lg,
        }}>
          <Card variant="default" padding="lg" hover>
            <div style={{
              width: '100%',
              height: '200px',
              backgroundColor: theme.colors.surfaceAlt,
              borderRadius: theme.borderRadius.md,
              marginBottom: theme.spacing.md,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <span style={{
                fontSize: theme.typography.fontSize['3xl'],
                color: theme.colors.textSecondary,
              }}>
                🤠
              </span>
            </div>
            <h3 style={{
              fontFamily: theme.typography.fontFamily.primary,
              fontSize: theme.typography.fontSize.xl,
              marginBottom: theme.spacing.sm,
              color: theme.colors.primaryDark,
            }}>
              Western Hat
            </h3>
            <p style={{
              fontFamily: theme.typography.fontFamily.secondary,
              fontSize: theme.typography.fontSize.sm,
              color: theme.colors.textSecondary,
              marginBottom: theme.spacing.md,
            }}>
              Authentic cowboy hat made from premium materials
            </p>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <span style={{
                fontFamily: theme.typography.fontFamily.primary,
                fontSize: theme.typography.fontSize.xl,
                fontWeight: theme.typography.fontWeight.bold,
                color: theme.colors.primary,
              }}>
                $89.99
              </span>
              <Button variant="cart" size="sm">Add to Cart</Button>
            </div>
          </Card>

          <Card variant="default" padding="lg" hover>
            <div style={{
              width: '100%',
              height: '200px',
              backgroundColor: theme.colors.surfaceAlt,
              borderRadius: theme.borderRadius.md,
              marginBottom: theme.spacing.md,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <span style={{
                fontSize: theme.typography.fontSize['3xl'],
                color: theme.colors.textSecondary,
              }}>
                👢
              </span>
            </div>
            <h3 style={{
              fontFamily: theme.typography.fontFamily.primary,
              fontSize: theme.typography.fontSize.xl,
              marginBottom: theme.spacing.sm,
              color: theme.colors.primaryDark,
            }}>
              Leather Boots
            </h3>
            <p style={{
              fontFamily: theme.typography.fontFamily.secondary,
              fontSize: theme.typography.fontSize.sm,
              color: theme.colors.textSecondary,
              marginBottom: theme.spacing.md,
            }}>
              Handcrafted leather boots with western styling
            </p>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <span style={{
                fontFamily: theme.typography.fontFamily.primary,
                fontSize: theme.typography.fontSize.xl,
                fontWeight: theme.typography.fontWeight.bold,
                color: theme.colors.primary,
              }}>
                $149.99
              </span>
              <Button variant="cart" size="sm">Add to Cart</Button>
            </div>
          </Card>

          <Card variant="default" padding="lg" hover>
            <div style={{
              width: '100%',
              height: '200px',
              backgroundColor: theme.colors.surfaceAlt,
              borderRadius: theme.borderRadius.md,
              marginBottom: theme.spacing.md,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <span style={{
                fontSize: theme.typography.fontSize['3xl'],
                color: theme.colors.textSecondary,
              }}>
                🎸
              </span>
            </div>
            <h3 style={{
              fontFamily: theme.typography.fontFamily.primary,
              fontSize: theme.typography.fontSize.xl,
              marginBottom: theme.spacing.sm,
              color: theme.colors.primaryDark,
            }}>
              Acoustic Guitar
            </h3>
            <p style={{
              fontFamily: theme.typography.fontFamily.secondary,
              fontSize: theme.typography.fontSize.sm,
              color: theme.colors.textSecondary,
              marginBottom: theme.spacing.md,
            }}>
              Classic western guitar with rich, warm tones
            </p>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <span style={{
                fontFamily: theme.typography.fontFamily.primary,
                fontSize: theme.typography.fontSize.xl,
                fontWeight: theme.typography.fontWeight.bold,
                color: theme.colors.primary,
              }}>
                $299.99
              </span>
              <Button variant="cart" size="sm">Add to Cart</Button>
            </div>
          </Card>
        </div>
      </Container>
    </div>
  );
};

export default StyleGuidePage;

